/**
 * Admin "Reports" page — `/ops/reports`.
 *
 * Renders daily counts for the ingestion funnel, event outcomes, discovery,
 * notifications and Azure resource-group cost (by service + per-AOAI-model).
 * All data comes from one API call: `GET /api/mgmt/reports/daily?days=N`,
 * which is cached server-side for 1h. We additionally cache client-side via
 * `swrCache` so revisits render instantly and revalidate in the background.
 *
 * Charts are inline SVG sparklines (`<Sparkline/>`) — no chart library, zero
 * extra bundle weight.
 *
 * Accessibility:
 * - One `<h1>` (the page heading).
 * - Each section is a real `<section>` with its own `<h2>`.
 * - Tables use `<caption>` + `<th scope="col">` so screen readers announce
 *   columns when navigating cells.
 * - The range selector is a button group with `aria-pressed`.
 * - Auto-refresh status uses `aria-live="polite"`.
 */

import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Sparkline from '../../components/Sparkline';
import DateQualitySummary, { type DateQualitySnapshot } from '../../components/DateQualitySummary';
import { LoadingBlock } from '../../components/Spinner';
import { api, isAuthPending } from '../../lib/api';
import { readSwrCache, writeSwrCache } from '../../lib/swrCache';

// ---------- API response types (mirror api/src/functions/admin-stats.ts) ----------

interface IngestionDay {
  date: string;
  scanned: number;
  processed: number;
  pending: number;
  failed: number;
  calendarPosts: number;
  gateSkipped: number;
  bySource: Record<string, number>;
}

interface EventDay {
  date: string;
  created: number;
  approved: number;
  published: number;
  rejected: number;
  duplicates: number;
  recurringInstances: number;
  rejectByCategory: Record<string, number>;
  venueIdentifyAttempted: number;
  venueIdentifyMatched: number;
  venueIdentifyCreated: number;
  venueIdentifyNoMatch: number;
}

interface DiscoveryDay {
  date: string;
  newVenues: number;
  newSeries: number;
}

interface NotificationDay {
  date: string;
  total: number;
  byChannel: Record<string, number>;
  byStatus: Record<string, number>;
}

interface CostDay {
  date: string;
  totalCost: number;
  byGroup: Record<string, number>;
}

interface PipelineStepDay {
  date: string;
  totalCalls: number;
  totalCostUsd: number;
  totalTokens: number;
  byStep: Record<
    string,
    { calls: number; costUsd: number; okCalls: number; failCalls: number }
  >;
}

interface PipelineStepTotal {
  step: string;
  service: string;
  calls: number;
  okCalls: number;
  failCalls: number;
  skippedCalls?: number;
  degradedCalls?: number;
  costUsd: number;
  meteredCostUsd?: number;
  listPriceUsd?: number;
  cashCostUsd?: number;
  cashKnownCalls?: number;
  unpricedCalls?: number;
  totalTokens: number;
  avgLatencyMs: number;
  topOutcomes: Array<{ outcome: string; count: number; costUsd: number }>;
}

interface ReportsDaily {
  generatedAt: string;
  range: { fromIso: string; toIso: string; days: number; timeZone: string };
  ingestion: IngestionDay[];
  events: EventDay[];
  discovery: DiscoveryDay[];
  notifications: NotificationDay[];
  cost: {
    currency: string;
    byService: CostDay[];
    byAoaiMeter: CostDay[];
    serviceError?: string;
    meterError?: string;
  };
  pipelineSteps: {
    days: PipelineStepDay[];
    totals: PipelineStepTotal[];
    grandTotalCalls: number;
    grandTotalCostUsd: number;
    grandTotalTokens: number;
    error?: string;
  };
  /** Latest public date/time badge snapshot, or null when the last two days hold none. */
  dateQuality: DateQualitySnapshot | null;
  totals: {
    ingestion: { scanned: number; processed: number; pending: number; failed: number; calendarPosts: number };
    events: { created: number; approved: number; published: number; rejected: number; duplicates: number };
    discovery: { newVenues: number; newSeries: number };
    notifications: { total: number; failed: number };
    costTotal: number;
    costByService: Array<{ group: string; cost: number }>;
    costByAoaiMeter: Array<{ group: string; cost: number }>;
  };
  cached?: boolean;
  cacheAgeSec?: number;
}

// ---------- Helpers ----------

const RANGE_OPTIONS: Array<{ days: number; label: string }> = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' }
];

function fmtMoney(n: number, currency: string): string {
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

/**
 * Money formatter that keeps fractional cents for small amounts (e.g.
 * `$0.0042`). Use for pipeline-step costs which are often well below 1¢.
 */
function fmtMoneyPrecise(n: number, currency: string): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  const max = abs > 0 && abs < 0.01 ? 4 : 2;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: max,
      minimumFractionDigits: max === 4 ? 4 : 2
    }).format(n);
  } catch {
    return `$${n.toFixed(max)}`;
  }
}

function fmtCount(n: number): string {
  return n.toLocaleString();
}

function fmtShortDate(yyyymmdd: string): string {
  // yyyymmdd = "2026-04-30" — render as "Apr 30" so column headers fit.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return yyyymmdd;
  const [, m, d] = yyyymmdd.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1] ?? m} ${Number(d)}`;
}

/**
 * Reduce a long meter name like "gpt-5.1 Inp glbl Tokens" to a stable model
 * key ("gpt-5.1") for friendlier rollups. Unknown shapes pass through.
 */
function meterToModel(meter: string): string {
  const lower = meter.toLowerCase();
  for (const m of ['gpt-5.1', 'gpt-5-mini', 'gpt-5', 'gpt-4o-mini', 'gpt-4o', 'gpt-4.1-mini', 'gpt-4.1', 'computer vision', 'image analysis']) {
    if (lower.includes(m)) return m;
  }
  return meter;
}

/**
 * Roll up `Array<{group, cost}>` by mapping each group via `keyFn` and summing.
 * Returns sorted desc by cost.
 */
function rollupBy<T extends { group: string; cost: number }>(
  items: T[],
  keyFn: (group: string) => string
): Array<{ group: string; cost: number }> {
  const map = new Map<string, number>();
  for (const it of items) {
    const k = keyFn(it.group);
    map.set(k, (map.get(k) ?? 0) + it.cost);
  }
  return Array.from(map.entries())
    .map(([group, cost]) => ({ group, cost }))
    .sort((a, b) => b.cost - a.cost);
}

// ---------- Section components ----------

function MetricTile({
  label,
  value,
  hint,
  variant
}: {
  label: string;
  value: string;
  hint?: string;
  variant?: 'success' | 'warning' | 'danger' | 'info';
}) {
  const borderClass = variant ? `border-${variant}` : '';
  return (
    <div className={`card h-100 ${borderClass}`}>
      <div className="card-body py-2 px-3">
        <div className="text-body-secondary small text-uppercase">
          {label}
        </div>
        <div className="fs-4 fw-semibold">{value}</div>
        {hint ? <div className="small text-body-secondary">{hint}</div> : null}
      </div>
    </div>
  );
}

function IngestionSection({ rows, currency: _c, timeZone }: { rows: IngestionDay[]; currency: string; timeZone: string }) {
  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) for (const k of Object.keys(r.bySource)) set.add(k);
    return Array.from(set).sort();
  }, [rows]);

  return (
    <section aria-labelledby="rep-ingest" className="mb-4">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h2 id="rep-ingest" className="h5 mb-0">Ingestion funnel</h2>
          <span className="small text-body-secondary">Per-day raw post counts ({timeZone})</span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <caption className="visually-hidden">Daily ingestion counts including scanned, processed, pending, failed, calendar posts, and gate-skipped</caption>
              <thead>
                <tr>
                  <th scope="col" className="text-nowrap">Day</th>
                  <th scope="col" className="text-end">Scanned</th>
                  <th scope="col" className="text-end">Processed</th>
                  <th scope="col" className="text-end">Pending</th>
                  <th scope="col" className="text-end">Failed</th>
                  <th scope="col" className="text-end">Calendar posts</th>
                  <th scope="col" className="text-end">Gate-skipped</th>
                  {sources.map((s) => (
                    <th scope="col" key={s} className="text-end text-nowrap">{s}</th>
                  ))}
                </tr>
                <tr aria-hidden="true">
                  <th scope="row" className="small text-body-secondary">Trend</th>
                  <td><Sparkline values={rows.map((r) => r.scanned)} label="Scanned per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.processed)} label="Processed per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.pending)} label="Pending per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.failed)} label="Failed per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.calendarPosts)} label="Calendar posts per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.gateSkipped)} label="Gate-skipped per day" /></td>
                  {sources.map((s) => (
                    <td key={s}><Sparkline values={rows.map((r) => r.bySource[s] ?? 0)} label={`${s} per day`} /></td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice().reverse().map((r) => (
                  <tr key={r.date}>
                    <th scope="row" className="text-nowrap">{fmtShortDate(r.date)}</th>
                    <td className="text-end">{fmtCount(r.scanned)}</td>
                    <td className="text-end">{fmtCount(r.processed)}</td>
                    <td className={`text-end ${r.pending > 0 ? 'text-warning-emphasis' : ''}`}>{fmtCount(r.pending)}</td>
                    <td className={`text-end ${r.failed > 0 ? 'text-danger-emphasis' : ''}`}>{fmtCount(r.failed)}</td>
                    <td className="text-end">{fmtCount(r.calendarPosts)}</td>
                    <td className="text-end">{fmtCount(r.gateSkipped)}</td>
                    {sources.map((s) => (
                      <td key={s} className="text-end text-body-secondary">{fmtCount(r.bySource[s] ?? 0)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function EventsSection({ rows }: { rows: EventDay[] }) {
  const rejectCats = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) for (const k of Object.keys(r.rejectByCategory)) set.add(k);
    return Array.from(set).sort();
  }, [rows]);

  return (
    <section aria-labelledby="rep-events" className="mb-4">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h2 id="rep-events" className="h5 mb-0">Event outcomes</h2>
          <span className="small text-body-secondary">Per-day created / approved / published / rejected (by category)</span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <caption className="visually-hidden">Daily event extraction outcomes</caption>
              <thead>
                <tr>
                  <th scope="col" className="text-nowrap">Day</th>
                  <th scope="col" className="text-end">Created</th>
                  <th scope="col" className="text-end">Approved</th>
                  <th scope="col" className="text-end">Published</th>
                  <th scope="col" className="text-end">Rejected</th>
                  <th scope="col" className="text-end">Duplicates</th>
                  <th scope="col" className="text-end">Recurring</th>
                  <th scope="col" className="text-end">Venue identify</th>
                  {rejectCats.map((c) => (
                    <th scope="col" key={c} className="text-end text-nowrap small">{c}</th>
                  ))}
                </tr>
                <tr aria-hidden="true">
                  <th scope="row" className="small text-body-secondary">Trend</th>
                  <td><Sparkline values={rows.map((r) => r.created)} label="Created per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.approved)} label="Approved per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.published)} label="Published per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.rejected)} label="Rejected per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.duplicates)} label="Duplicates per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.recurringInstances)} label="Recurring instances per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.venueIdentifyAttempted)} label="Venue identifications per day" /></td>
                  {rejectCats.map((c) => (
                    <td key={c}><Sparkline values={rows.map((r) => r.rejectByCategory[c] ?? 0)} label={`${c} per day`} /></td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice().reverse().map((r) => (
                  <tr key={r.date}>
                    <th scope="row" className="text-nowrap">{fmtShortDate(r.date)}</th>
                    <td className="text-end">{fmtCount(r.created)}</td>
                    <td className="text-end">{fmtCount(r.approved)}</td>
                    <td className="text-end">{fmtCount(r.published)}</td>
                    <td className={`text-end ${r.rejected > 0 ? 'text-warning-emphasis' : ''}`}>{fmtCount(r.rejected)}</td>
                    <td className="text-end text-body-secondary">{fmtCount(r.duplicates)}</td>
                    <td className="text-end text-body-secondary">{fmtCount(r.recurringInstances)}</td>
                    <td className="text-end text-body-secondary" title={`matched ${r.venueIdentifyMatched} • created ${r.venueIdentifyCreated} • no-match ${r.venueIdentifyNoMatch}`}>
                      {fmtCount(r.venueIdentifyAttempted)}
                    </td>
                    {rejectCats.map((c) => (
                      <td key={c} className="text-end text-body-secondary">{fmtCount(r.rejectByCategory[c] ?? 0)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function DiscoverySection({ rows }: { rows: DiscoveryDay[] }) {
  return (
    <section aria-labelledby="rep-discovery" className="mb-4">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h2 id="rep-discovery" className="h5 mb-0">Discovery</h2>
          <span className="small text-body-secondary">New venues / series candidates added per day</span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <caption className="visually-hidden">Daily venue and series discovery counts</caption>
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col" className="text-end">New venues</th>
                  <th scope="col" className="text-end">New series</th>
                </tr>
                <tr aria-hidden="true">
                  <th scope="row" className="small text-body-secondary">Trend</th>
                  <td><Sparkline values={rows.map((r) => r.newVenues)} label="New venues per day" /></td>
                  <td><Sparkline values={rows.map((r) => r.newSeries)} label="New series per day" /></td>
                </tr>
              </thead>
              <tbody>
                {rows.slice().reverse().map((r) => (
                  <tr key={r.date}>
                    <th scope="row" className="text-nowrap">{fmtShortDate(r.date)}</th>
                    <td className="text-end">{fmtCount(r.newVenues)}</td>
                    <td className="text-end">{fmtCount(r.newSeries)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function NotificationsSection({ rows }: { rows: NotificationDay[] }) {
  const channels = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) for (const k of Object.keys(r.byChannel)) set.add(k);
    return Array.from(set).sort();
  }, [rows]);
  const statuses = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) for (const k of Object.keys(r.byStatus)) set.add(k);
    return Array.from(set).sort();
  }, [rows]);

  return (
    <section aria-labelledby="rep-notifs" className="mb-4">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h2 id="rep-notifs" className="h5 mb-0">Notifications</h2>
          <span className="small text-body-secondary">Subscription deliveries by channel and status</span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <caption className="visually-hidden">Daily subscription delivery counts split by channel and outcome status</caption>
              <thead>
                <tr>
                  <th scope="col">Day</th>
                  <th scope="col" className="text-end">Total</th>
                  {channels.map((c) => (
                    <th scope="col" key={c} className="text-end text-nowrap">{c}</th>
                  ))}
                  {statuses.map((s) => (
                    <th scope="col" key={s} className="text-end text-nowrap">{s}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice().reverse().map((r) => (
                  <tr key={r.date}>
                    <th scope="row" className="text-nowrap">{fmtShortDate(r.date)}</th>
                    <td className="text-end">{fmtCount(r.total)}</td>
                    {channels.map((c) => (
                      <td key={c} className="text-end text-body-secondary">{fmtCount(r.byChannel[c] ?? 0)}</td>
                    ))}
                    {statuses.map((s) => {
                      const v = r.byStatus[s] ?? 0;
                      const cls = s === 'failed' && v > 0 ? 'text-danger-emphasis' : 'text-body-secondary';
                      return (
                        <td key={s} className={`text-end ${cls}`}>{fmtCount(v)}</td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function PipelineStepsSection({
  days,
  totals,
  grandTotalCalls,
  grandTotalCostUsd,
  grandTotalTokens,
  error
}: {
  days: PipelineStepDay[];
  totals: PipelineStepTotal[];
  grandTotalCalls: number;
  grandTotalCostUsd: number;
  grandTotalTokens: number;
  error?: string;
}) {
  // Currency for pipeline-step costs is always USD (provider list price).
  const currency = 'USD';

  // Top 5 steps by cost get their own column. Everything else collapses into
  // an "other" bucket so the table doesn't get unwieldy.
  const TOP_N = 5;
  const topSteps = useMemo(() => totals.slice(0, TOP_N).map((t) => t.step), [totals]);
  const topSet = useMemo(() => new Set(topSteps), [topSteps]);

  return (
    <section aria-labelledby="rep-pipeline" className="mb-4">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h2 id="rep-pipeline" className="h5 mb-0">Pipeline telemetry (usage)</h2>
          <span className="small text-body-secondary">
            Daily aggregates refresh every three hours. Usage attribution is not a cash invoice; estimates, free tiers, and credits remain separate.
          </span>
        </div>
        {error ? (
          <div className="card-body pb-0">
            <div className="alert alert-warning mb-0" role="status">
              Pipeline telemetry unavailable: {error}
            </div>
          </div>
        ) : null}

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <caption className="visually-hidden">Daily pipeline-step calls and attributed usage cost, not cash charges</caption>
              <thead>
                <tr>
                  <th scope="col" className="text-nowrap">Day</th>
                  <th scope="col" className="text-end">Attributed cost</th>
                  <th scope="col" className="text-end">Step records</th>
                  <th scope="col" className="text-end">Tokens</th>
                  {topSteps.map((s) => (
                    <th scope="col" key={s} className="text-end text-nowrap">{s}</th>
                  ))}
                  <th scope="col" className="text-end text-nowrap">other</th>
                </tr>
                <tr aria-hidden="true">
                  <th scope="row" className="small text-body-secondary">Trend</th>
                  <td><Sparkline values={days.map((d) => d.totalCostUsd)} label="Total cost per day" /></td>
                  <td><Sparkline values={days.map((d) => d.totalCalls)} label="Total calls per day" /></td>
                  <td><Sparkline values={days.map((d) => d.totalTokens)} label="Total tokens per day" /></td>
                  {topSteps.map((s) => (
                    <td key={s}>
                      <Sparkline
                        values={days.map((d) => d.byStep[s]?.costUsd ?? 0)}
                        label={`${s} cost per day`}
                      />
                    </td>
                  ))}
                  <td>
                    <Sparkline
                      values={days.map((d) => {
                        let other = 0;
                        for (const k of Object.keys(d.byStep)) {
                          if (!topSet.has(k)) other += d.byStep[k]!.costUsd;
                        }
                        return other;
                      })}
                      label="Other steps cost per day"
                    />
                  </td>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  let otherCost = 0;
                  for (const k of Object.keys(d.byStep)) {
                    if (!topSet.has(k)) otherCost += d.byStep[k]!.costUsd;
                  }
                  return (
                    <tr key={d.date}>
                      <th scope="row" className="text-nowrap">{fmtShortDate(d.date)}</th>
                      <td className="text-end">{fmtMoneyPrecise(d.totalCostUsd, currency)}</td>
                      <td className="text-end">{fmtCount(d.totalCalls)}</td>
                      <td className="text-end">{fmtCount(d.totalTokens)}</td>
                      {topSteps.map((s) => (
                        <td key={s} className="text-end">
                          {fmtMoneyPrecise(d.byStep[s]?.costUsd ?? 0, currency)}
                        </td>
                      ))}
                      <td className="text-end">{fmtMoneyPrecise(otherCost, currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="table-light fw-semibold">
                  <th scope="row">Period</th>
                  <td className="text-end">{fmtMoneyPrecise(grandTotalCostUsd, currency)}</td>
                  <td className="text-end">{fmtCount(grandTotalCalls)}</td>
                  <td className="text-end">{fmtCount(grandTotalTokens)}</td>
                  {topSteps.map((s) => {
                    const t = totals.find((x) => x.step === s);
                    return (
                      <td key={s} className="text-end">
                        {fmtMoneyPrecise(t?.costUsd ?? 0, currency)}
                      </td>
                    );
                  })}
                  <td className="text-end">
                    {fmtMoneyPrecise(
                      totals.filter((t) => !topSet.has(t.step)).reduce((a, t) => a + t.costUsd, 0),
                      currency
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="card-body p-0 border-top">
          <div className="px-3 py-2 small text-body-secondary">
            Per-step rollup for the period (top 20 by cost)
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <caption className="visually-hidden">Pipeline step rollup with calls, success rate, cost, and latency</caption>
              <thead>
                <tr>
                  <th scope="col">Step</th>
                  <th scope="col">Service</th>
                  <th scope="col" className="text-end">Calls</th>
                  <th scope="col" className="text-end">Succeeded %</th>
                  <th scope="col" className="text-end">Failed</th>
                  <th scope="col" className="text-end">Skipped / fallback</th>
                  <th scope="col" className="text-end">Attributed</th>
                  <th scope="col" className="text-end">Metered / estimated usage</th>
                  <th scope="col" className="text-end">Cash attribution</th>
                  <th scope="col" className="text-end">Tokens</th>
                  <th scope="col" className="text-end">Avg latency</th>
                  <th scope="col">Top outcomes</th>
                </tr>
              </thead>
              <tbody>
                {totals.slice(0, 20).map((t) => {
                  const okPct = t.calls > 0 ? Math.round((t.okCalls / t.calls) * 100) : 0;
                  return (
                    <tr key={t.step}>
                      <th scope="row" className="text-nowrap">{t.step}</th>
                      <td className="text-nowrap">{t.service || '—'}</td>
                      <td className="text-end">{fmtCount(t.calls)}</td>
                      <td className="text-end">{okPct}%</td>
                      <td className="text-end">{fmtCount(t.failCalls)}</td>
                      <td className="text-end">{fmtCount(t.skippedCalls ?? 0)} / {fmtCount(t.degradedCalls ?? 0)}</td>
                      <td className="text-end">{fmtMoneyPrecise(t.costUsd, currency)}</td>
                      <td className="text-end">
                        {fmtMoneyPrecise(t.meteredCostUsd ?? t.costUsd, currency)}
                        {(t.unpricedCalls ?? 0) > 0
                          ? <span className="d-block small text-body-secondary">+ {fmtCount(t.unpricedCalls ?? 0)} unpriced</span>
                          : null}
                      </td>
                      <td className="text-end">
                        {t.cashKnownCalls === t.calls && t.cashCostUsd !== undefined
                          ? fmtMoneyPrecise(t.cashCostUsd, currency)
                          : 'Unknown'}
                      </td>
                      <td className="text-end">{fmtCount(t.totalTokens)}</td>
                      <td className="text-end">{fmtCount(t.avgLatencyMs)} ms</td>
                      <td className="small text-body-secondary">
                        {t.topOutcomes.length === 0
                          ? '—'
                          : t.topOutcomes
                              .map((o) => `${o.outcome} (${fmtCount(o.count)})`)
                              .join(', ')}
                      </td>
                    </tr>
                  );
                })}
                {totals.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center text-body-secondary py-3">
                      No pipeline-step telemetry recorded in this window.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

function CostSection({
  byService,
  byAoaiMeter,
  currency,
  serviceError,
  meterError,
  totals
}: {
  byService: CostDay[];
  byAoaiMeter: CostDay[];
  currency: string;
  serviceError?: string;
  meterError?: string;
  totals: ReportsDaily['totals'];
}) {
  const services = useMemo(() => {
    const set = new Set<string>();
    for (const r of byService) for (const k of Object.keys(r.byGroup)) set.add(k);
    return Array.from(set).sort();
  }, [byService]);

  const modelRollupDays = useMemo(() => {
    return byAoaiMeter.map((day) => {
      const byModel: Record<string, number> = {};
      let totalCost = 0;
      for (const [meter, cost] of Object.entries(day.byGroup)) {
        const k = meterToModel(meter);
        byModel[k] = (byModel[k] ?? 0) + cost;
        totalCost += cost;
      }
      return { date: day.date, totalCost, byGroup: byModel };
    });
  }, [byAoaiMeter]);

  const models = useMemo(() => {
    const set = new Set<string>();
    for (const r of modelRollupDays) for (const k of Object.keys(r.byGroup)) set.add(k);
    return Array.from(set).sort();
  }, [modelRollupDays]);

  const modelTotals = useMemo(() => rollupBy(totals.costByAoaiMeter, meterToModel), [totals.costByAoaiMeter]);

  return (
    <section aria-labelledby="rep-cost" className="mb-4">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h2 id="rep-cost" className="h5 mb-0">Azure cost (resource group)</h2>
          <span className="small text-body-secondary">
            Cost data refreshes 8–24 hours behind real time. Shown in {currency}.
          </span>
        </div>
        {(serviceError || meterError) ? (
          <div className="card-body pb-0">
            {serviceError ? (
              <div className="alert alert-warning small mb-2" role="alert">
                Cost-by-service unavailable: {serviceError}
              </div>
            ) : null}
            {meterError ? (
              <div className="alert alert-warning small mb-2" role="alert">
                Cost-by-AOAI-meter unavailable: {meterError}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="card-body p-0">
          {/* By service */}
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 align-middle">
              <caption className="visually-hidden">Daily Azure cost broken down by service name</caption>
              <thead>
                <tr>
                  <th scope="col" className="text-nowrap">Day</th>
                  <th scope="col" className="text-end">Total</th>
                  {services.map((s) => (
                    <th scope="col" key={s} className="text-end text-nowrap small">{s}</th>
                  ))}
                </tr>
                <tr aria-hidden="true">
                  <th scope="row" className="small text-body-secondary">Trend</th>
                  <td><Sparkline values={byService.map((r) => r.totalCost)} label="Total cost per day" /></td>
                  {services.map((s) => (
                    <td key={s}><Sparkline values={byService.map((r) => r.byGroup[s] ?? 0)} label={`${s} per day`} /></td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byService.slice().reverse().map((r) => (
                  <tr key={r.date}>
                    <th scope="row" className="text-nowrap">{fmtShortDate(r.date)}</th>
                    <td className="text-end fw-semibold">{fmtMoney(r.totalCost, currency)}</td>
                    {services.map((s) => (
                      <td key={s} className="text-end text-body-secondary">{fmtMoney(r.byGroup[s] ?? 0, currency)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" className="text-nowrap">Period total</th>
                  <td className="text-end fw-bold">{fmtMoney(totals.costTotal, currency)}</td>
                  {services.map((s) => {
                    const total = totals.costByService.find((t) => t.group === s)?.cost ?? 0;
                    return (
                      <td key={s} className="text-end fw-semibold">{fmtMoney(total, currency)}</td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>

          {/* By AOAI model */}
          {models.length > 0 ? (
            <>
              <div className="px-3 pt-3 pb-2 border-top">
                <h3 className="h6 mb-0">Azure OpenAI per-model breakdown</h3>
                <p className="small text-body-secondary mb-0">Meter names rolled up to model. Includes input + output + cached tokens.</p>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0 align-middle">
                  <caption className="visually-hidden">Daily Azure OpenAI cost per model</caption>
                  <thead>
                    <tr>
                      <th scope="col" className="text-nowrap">Day</th>
                      <th scope="col" className="text-end">AOAI total</th>
                      {models.map((m) => (
                        <th scope="col" key={m} className="text-end text-nowrap small">{m}</th>
                      ))}
                    </tr>
                    <tr aria-hidden="true">
                      <th scope="row" className="small text-body-secondary">Trend</th>
                      <td><Sparkline values={modelRollupDays.map((r) => r.totalCost)} label="AOAI total per day" /></td>
                      {models.map((m) => (
                        <td key={m}><Sparkline values={modelRollupDays.map((r) => r.byGroup[m] ?? 0)} label={`${m} per day`} /></td>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modelRollupDays.slice().reverse().map((r) => (
                      <tr key={r.date}>
                        <th scope="row" className="text-nowrap">{fmtShortDate(r.date)}</th>
                        <td className="text-end fw-semibold">{fmtMoney(r.totalCost, currency)}</td>
                        {models.map((m) => (
                          <td key={m} className="text-end text-body-secondary">{fmtMoney(r.byGroup[m] ?? 0, currency)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row" className="text-nowrap">Period total</th>
                      <td className="text-end fw-bold">
                        {fmtMoney(modelTotals.reduce((a, m) => a + m.cost, 0), currency)}
                      </td>
                      {models.map((m) => {
                        const t = modelTotals.find((x) => x.group === m)?.cost ?? 0;
                        return (
                          <td key={m} className="text-end fw-semibold">{fmtMoney(t, currency)}</td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ---------- Page ----------

/**
 * IANA timezone of the current browser. Falls back to America/New_York if the
 * runtime can't resolve one (very old browsers). The value is sent to the
 * backend so per-day buckets align with the viewer's local calendar instead
 * of always using NY time.
 */
const USER_TZ: string = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
})();

const SWR_KEY = (days: number, tz: string) => `mhe.reports.daily.v1.${days}d.${tz}`;

export default function AdminReportsPage() {
  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<ReportsDaily | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Hydrate from SWR cache the moment `days` changes; then revalidate.
  useEffect(() => {
    let cancelled = false;
    const cached = readSwrCache<ReportsDaily>(SWR_KEY(days, USER_TZ));
    if (cached) {
      setData(cached);
      setLoading(false);
    } else {
      setData(null);
      setLoading(true);
    }
    setErr(null);
    setRefreshing(true);
    api
      .get<ReportsDaily>(`/api/mgmt/reports/daily?days=${days}&tz=${encodeURIComponent(USER_TZ)}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        writeSwrCache(SWR_KEY(days, USER_TZ), res);
        setLoading(false);
        setRefreshing(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRefreshing(false);
        setLoading(false);
        if (isAuthPending(e)) return; // session-expired modal handles this
        setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const onForceRefresh = () => {
    setRefreshing(true);
    setErr(null);
    api
      .get<ReportsDaily>(`/api/mgmt/reports/daily?days=${days}&tz=${encodeURIComponent(USER_TZ)}&refresh=1`)
      .then((res) => {
        setData(res);
        writeSwrCache(SWR_KEY(days, USER_TZ), res);
        setRefreshing(false);
      })
      .catch((e: unknown) => {
        setRefreshing(false);
        if (isAuthPending(e)) return;
        setErr(e instanceof Error ? e.message : String(e));
      });
  };

  return (
    <AdminLayout>
      <header className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
          <h1 className="h3 mb-1">Reports</h1>
          <p className="text-body-secondary mb-0 small">
            Daily numbers across the pipeline plus Azure resource-group cost. Cached for up to one hour;
            cost data lags 8–24 hours behind live usage.
          </p>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="btn-group" role="group" aria-label="Time range">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                type="button"
                className={`btn btn-sm ${opt.days === days ? 'btn-primary' : 'btn-outline-primary'}`}
                aria-current={opt.days === days ? 'true' : undefined}
                onClick={() => setDays(opt.days)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onForceRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </header>

      <div className="visually-hidden" aria-live="polite">
        {refreshing ? 'Refreshing reports' : data ? `Reports loaded for last ${days} days` : ''}
      </div>

      {err ? (
        <div className="alert alert-danger" role="alert">{err}</div>
      ) : null}

      {loading && !data ? (
        <LoadingBlock label="Loading reports…" />
      ) : data ? (
        <>
          {/* Summary header strip */}
          <div className="row row-cols-2 row-cols-md-3 row-cols-lg-6 g-2 mb-3">
            <div className="col">
              <MetricTile label="Posts scanned" value={fmtCount(data.totals.ingestion.scanned)} hint={`${fmtCount(data.totals.ingestion.processed)} processed`} />
            </div>
            <div className="col">
              <MetricTile
                label="Pending raws"
                value={fmtCount(data.totals.ingestion.pending)}
                variant={data.totals.ingestion.pending > 0 ? 'warning' : undefined}
                hint={`${fmtCount(data.totals.ingestion.failed)} failed`}
              />
            </div>
            <div className="col">
              <MetricTile label="Events published" value={fmtCount(data.totals.events.published)} hint={`${fmtCount(data.totals.events.created)} created`} />
            </div>
            <div className="col">
              <MetricTile label="Events rejected" value={fmtCount(data.totals.events.rejected)} hint={`${fmtCount(data.totals.events.duplicates)} duplicates`} />
            </div>
            <div className="col">
              <MetricTile label="New venues / series" value={`${fmtCount(data.totals.discovery.newVenues)} / ${fmtCount(data.totals.discovery.newSeries)}`} />
            </div>
            <div className="col">
              <MetricTile
                label="Azure cost (period)"
                value={fmtMoney(data.totals.costTotal, data.cost.currency)}
                hint={`${fmtCount(data.totals.notifications.total)} notifications sent`}
                variant="info"
              />
            </div>
            <div className="col">
              <MetricTile
                label="Pipeline cost (live)"
                value={fmtMoneyPrecise(data.pipelineSteps.grandTotalCostUsd, 'USD')}
                hint={`${fmtCount(data.pipelineSteps.grandTotalCalls)} calls • ${fmtCount(data.pipelineSteps.grandTotalTokens)} tokens`}
                variant={data.pipelineSteps.error ? 'warning' : 'info'}
              />
            </div>
          </div>

          <p className="small text-body-secondary mb-3">
            Generated {new Date(data.generatedAt).toLocaleString()} •
            range {data.range.fromIso.slice(0, 10)} → {data.range.toIso.slice(0, 10)} •
            timezone {data.range.timeZone}
            {data.cached ? ` • served from server cache (${data.cacheAgeSec}s old)` : ''}
          </p>

          <DateQualitySummary snapshot={data.dateQuality} />

          <IngestionSection rows={data.ingestion} currency={data.cost.currency} timeZone={data.range.timeZone} />
          <EventsSection rows={data.events} />
          <DiscoverySection rows={data.discovery} />
          <NotificationsSection rows={data.notifications} />
          <CostSection
            byService={data.cost.byService}
            byAoaiMeter={data.cost.byAoaiMeter}
            currency={data.cost.currency}
            serviceError={data.cost.serviceError}
            meterError={data.cost.meterError}
            totals={data.totals}
          />
          <PipelineStepsSection
            days={data.pipelineSteps.days}
            totals={data.pipelineSteps.totals}
            grandTotalCalls={data.pipelineSteps.grandTotalCalls}
            grandTotalCostUsd={data.pipelineSteps.grandTotalCostUsd}
            grandTotalTokens={data.pipelineSteps.grandTotalTokens}
            error={data.pipelineSteps.error}
          />
        </>
      ) : null}
    </AdminLayout>
  );
}
